<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import PageHeader from 'dashboard/components-next/teletel/PageHeader.vue';
import ContentCard from 'dashboard/components-next/teletel/ContentCard.vue';
import LifecycleStageBadge from 'dashboard/components-next/teletel/lifecycle/LifecycleStageBadge.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CustomerLifecycleAPI from 'dashboard/api/teletel/customerLifecycle';
import PageTabs from 'dashboard/components-next/teletel/PageTabs.vue';
import { useTeletelPageTabs } from 'dashboard/composables/useTeletelPageTabs';

const { lifecycleTabs } = useTeletelPageTabs();

const { t } = useI18n();
const store = useStore();

const CATEGORIES = ['active', 'won', 'lost'];
const DEFAULT_COLOR = '#64748B';

const showArchived = ref(false);
const editingId = ref(null);
const stagesLoadState = ref('idle');
const form = reactive({
  name: '',
  description: '',
  emoji: '',
  color: DEFAULT_COLOR,
  category: 'active',
  is_default: false,
});

const settings = computed(
  () => store.getters['customerLifecycle/getSettings'] || {}
);
const allStages = computed(
  () => store.getters['customerLifecycle/getStages'] || []
);
const activeStages = computed(() =>
  allStages.value
    .filter(stage => !stage.archived_at)
    .sort((a, b) => a.position - b.position)
);
const archivedStages = computed(() =>
  allStages.value.filter(stage => stage.archived_at)
);
const displayedStages = computed(() =>
  showArchived.value
    ? [...activeStages.value, ...archivedStages.value]
    : activeStages.value
);
const uiFlags = computed(
  () => store.getters['customerLifecycle/getUIFlags'] || {}
);
const isLoadingSettings = computed(() => uiFlags.value.isFetchingSettings);
const isLoadingStages = computed(
  () => stagesLoadState.value === 'loading' || uiFlags.value.isFetchingStages
);
const stagesReady = computed(
  () =>
    stagesLoadState.value === 'loaded' &&
    store.getters['customerLifecycle/areStagesLoaded']
);
const stageMutationBusy = computed(
  () =>
    !stagesReady.value ||
    uiFlags.value.isSavingStage ||
    uiFlags.value.isReordering
);
const canSave = computed(
  () =>
    stagesReady.value && Boolean(form.name.trim()) && !stageMutationBusy.value
);
const editingStage = computed(() =>
  allStages.value.find(stage => stage.id === editingId.value)
);
const isEditingDefault = computed(() =>
  Boolean(editingStage.value?.is_default)
);

watch(
  () => form.category,
  category => {
    if (category !== 'active' && !isEditingDefault.value) {
      form.is_default = false;
    }
  }
);

const resetForm = () => {
  editingId.value = null;
  Object.assign(form, {
    name: '',
    description: '',
    emoji: '',
    color: DEFAULT_COLOR,
    category: 'active',
    is_default: false,
  });
};

const loadStages = async () => {
  stagesLoadState.value = 'loading';
  try {
    await store.dispatch('customerLifecycle/getStages', {
      includeArchived: true,
      force: true,
    });
    stagesLoadState.value = store.getters['customerLifecycle/areStagesLoaded']
      ? 'loaded'
      : 'error';
  } catch (error) {
    stagesLoadState.value = 'error';
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_LIFECYCLE.ERROR.LOAD')
    );
  }
};

const load = async () => {
  try {
    const loadedSettings = await store.dispatch(
      'customerLifecycle/getSettings',
      { force: true }
    );
    if (loadedSettings?.available && loadedSettings?.enabled) {
      store.dispatch('labels/get');
      await loadStages();
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_LIFECYCLE.ERROR.LOAD')
    );
  }
};

onMounted(load);

// ---- automatic rules (owner order 2026-09-21) ----------------------------
const labels = computed(() => store.getters['labels/getLabels'] || []);
const rulesForm = reactive({
  first_message: { enabled: false, stage_id: '' },
  label_to_stage: { enabled: false, pairs: [] },
  inactivity: { enabled: false, days: 14, stage_id: '' },
});
const rulesSaving = ref(false);
const seedRules = () => {
  const rules = settings.value?.auto_rules;
  if (!rules) return;
  rulesForm.first_message = {
    enabled: Boolean(rules.first_message?.enabled),
    stage_id: rules.first_message?.stage_id || '',
  };
  rulesForm.label_to_stage = {
    enabled: Boolean(rules.label_to_stage?.enabled),
    pairs: (rules.label_to_stage?.pairs || []).map(pair => ({ ...pair })),
  };
  rulesForm.inactivity = {
    enabled: Boolean(rules.inactivity?.enabled),
    days: rules.inactivity?.days || 14,
    stage_id: rules.inactivity?.stage_id || '',
  };
};
watch(() => settings.value?.auto_rules, seedRules, { immediate: true });
const addPair = () =>
  rulesForm.label_to_stage.pairs.push({ label: '', stage_id: '' });
const removePair = index => rulesForm.label_to_stage.pairs.splice(index, 1);
const terminalOrActiveStages = computed(() => activeStages.value);
const saveRules = async () => {
  rulesSaving.value = true;
  try {
    await store.dispatch('customerLifecycle/updateAutoRules', {
      first_message: {
        enabled: rulesForm.first_message.enabled,
        stage_id: rulesForm.first_message.stage_id || null,
      },
      label_to_stage: {
        enabled: rulesForm.label_to_stage.enabled,
        pairs: rulesForm.label_to_stage.pairs.filter(
          pair => pair.label && pair.stage_id
        ),
      },
      inactivity: {
        enabled: rulesForm.inactivity.enabled,
        days: Number(rulesForm.inactivity.days) || 14,
        stage_id: rulesForm.inactivity.stage_id || null,
      },
    });
    useAlert(t('TELETEL_LIFECYCLE.SETTINGS.RULES.SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        t('TELETEL_LIFECYCLE.SETTINGS.RULES.ERROR')
    );
  } finally {
    rulesSaving.value = false;
  }
};

const toggleEnabled = async event => {
  const checkbox = event.target;
  const enabled = event.target.checked;
  try {
    await store.dispatch('customerLifecycle/updateSettings', enabled);
  } catch (error) {
    checkbox.checked = Boolean(settings.value.enabled);
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_LIFECYCLE.ERROR.SAVE')
    );
    return;
  }

  useAlert(
    t(
      enabled
        ? 'TELETEL_LIFECYCLE.SETTINGS.ENABLED'
        : 'TELETEL_LIFECYCLE.SETTINGS.DISABLED'
    )
  );
  if (enabled) {
    await loadStages();
  } else {
    resetForm();
    stagesLoadState.value = 'idle';
  }
};

const editStage = stage => {
  if (!stagesReady.value || stageMutationBusy.value) return;
  editingId.value = stage.id;
  Object.assign(form, {
    name: stage.name || '',
    description: stage.description || '',
    emoji: stage.emoji || '',
    color: stage.color || DEFAULT_COLOR,
    category: stage.category || 'active',
    is_default: Boolean(stage.is_default),
  });
};

const stagePayload = () => ({
  name: form.name.trim(),
  description: form.description.trim(),
  emoji: form.emoji.trim(),
  color: form.color,
  category: form.category,
  is_default: form.is_default,
});

const saveStage = async () => {
  if (!stagesReady.value || !canSave.value) return;
  try {
    if (editingId.value) {
      await store.dispatch('customerLifecycle/updateStage', {
        id: editingId.value,
        ...stagePayload(),
      });
    } else {
      await store.dispatch('customerLifecycle/createStage', stagePayload());
    }
    resetForm();
    useAlert(t('TELETEL_LIFECYCLE.STAGES.SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_LIFECYCLE.ERROR.SAVE_STAGE')
    );
  }
};

const archiveStage = async stage => {
  if (!stagesReady.value || stageMutationBusy.value) return;
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      t('TELETEL_LIFECYCLE.STAGES.ARCHIVE_CONFIRM', { name: stage.name })
    )
  ) {
    return;
  }
  try {
    await store.dispatch('customerLifecycle/archiveStage', stage.id);
    useAlert(t('TELETEL_LIFECYCLE.STAGES.ARCHIVED_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_LIFECYCLE.ERROR.ARCHIVE_STAGE')
    );
  }
};

// «حذف» beside archive (owner order 2026-09-27): move the contacts of the
// stage, all at once or one by one, then delete the stage for good.
const DELETE_PAGE_SIZE = 50;
const deleteDialog = ref(null);
const deleteTarget = ref(null);
const deleteMode = ref('all');
const deleteMoveTo = ref('');
const deleteContacts = ref([]);
const deleteContactsHasMore = ref(false);
const deleteContactsPage = ref(1);
const deleteContactsLoading = ref(false);
const deleteRemaining = ref(0);
const deleteBusy = ref(false);
const movingContactId = ref(null);
const movedCount = ref(0);
const contactTargets = reactive({});

const deleteTargets = computed(() =>
  activeStages.value.filter(stage => stage.id !== deleteTarget.value?.id)
);
const canConfirmDelete = computed(() => {
  if (!deleteTarget.value || deleteBusy.value) return false;
  if (deleteRemaining.value <= 0) return true;
  return deleteMode.value === 'all' && Boolean(deleteMoveTo.value);
});

const contactLabel = contact =>
  contact.name || contact.phone_number || contact.email || `#${contact.id}`;

const newOperationUuid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const loadDeleteContacts = async page => {
  if (!deleteTarget.value) return;
  deleteContactsLoading.value = true;
  try {
    const { data } = await CustomerLifecycleAPI.getBoardContacts({
      stage_id: deleteTarget.value.id,
      activity: 'all',
      per_page: DELETE_PAGE_SIZE,
      page,
    });
    const rows = data?.payload || [];
    rows.forEach(row => {
      if (!(row.id in contactTargets)) contactTargets[row.id] = '';
    });
    deleteContacts.value =
      page === 1 ? rows : [...deleteContacts.value, ...rows];
    deleteContactsHasMore.value = Boolean(data?.meta?.has_more);
    deleteContactsPage.value = page;
  } catch (error) {
    useAlert(t('TELETEL_LIFECYCLE.ERROR.LOAD_STAGE_CONTACTS'));
  } finally {
    deleteContactsLoading.value = false;
  }
};

const openDeleteDialog = async stage => {
  if (!stagesReady.value || stageMutationBusy.value) return;
  deleteTarget.value = stage;
  deleteMode.value = 'all';
  const fallback =
    deleteTargets.value.find(item => item.is_default) || deleteTargets.value[0];
  deleteMoveTo.value = fallback ? String(fallback.id) : '';
  deleteContacts.value = [];
  deleteContactsHasMore.value = false;
  deleteContactsPage.value = 1;
  deleteRemaining.value = stage.contacts_count ?? 0;
  movedCount.value = 0;
  Object.keys(contactTargets).forEach(key => delete contactTargets[key]);
  deleteDialog.value?.open();
  if (deleteRemaining.value > 0) await loadDeleteContacts(1);
};

const closeDeleteDialog = () => {
  deleteTarget.value = null;
  deleteContacts.value = [];
  // Contacts moved one by one changed the counts even if nothing was deleted.
  if (movedCount.value > 0) loadStages();
};

const moveOneContact = async contact => {
  const toStageId = contactTargets[contact.id];
  if (!toStageId || !deleteTarget.value || movingContactId.value) return;
  movingContactId.value = contact.id;
  try {
    const results = await store.dispatch('customerLifecycle/bulkTransition', {
      contactIds: [contact.id],
      toStageId,
      operationUuid: newOperationUuid(),
      expectedFromStageId: deleteTarget.value.id,
    });
    const status = results?.[0]?.status;
    if (status && !['transitioned', 'no_op', 'conflict'].includes(status)) {
      throw new Error(results[0].error || status);
    }
    deleteContacts.value = deleteContacts.value.filter(
      item => item.id !== contact.id
    );
    deleteRemaining.value = Math.max(0, deleteRemaining.value - 1);
    movedCount.value += 1;
    useAlert(t('TELETEL_LIFECYCLE.STAGES.MOVED_ONE'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_LIFECYCLE.ERROR.MOVE_CONTACT')
    );
  } finally {
    movingContactId.value = null;
  }
};

const confirmDelete = async () => {
  if (!canConfirmDelete.value) return;
  const stage = deleteTarget.value;
  const moveAll = deleteRemaining.value > 0 && deleteMode.value === 'all';
  deleteBusy.value = true;
  try {
    const result = await store.dispatch('customerLifecycle/deleteStage', {
      id: stage.id,
      moveToStageId: moveAll ? deleteMoveTo.value : undefined,
    });
    useAlert(
      t(
        result?.queued
          ? 'TELETEL_LIFECYCLE.STAGES.DELETE_QUEUED_MESSAGE'
          : 'TELETEL_LIFECYCLE.STAGES.DELETED_MESSAGE'
      )
    );
    deleteDialog.value?.close();
    await loadStages();
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_LIFECYCLE.ERROR.DELETE_STAGE')
    );
  } finally {
    deleteBusy.value = false;
  }
};

const moveStage = async (stage, offset) => {
  if (!stagesReady.value || stageMutationBusy.value) return;
  const index = activeStages.value.findIndex(item => item.id === stage.id);
  const target = index + offset;
  if (index < 0 || target < 0 || target >= activeStages.value.length) return;

  const ids = activeStages.value.map(item => item.id);
  [ids[index], ids[target]] = [ids[target], ids[index]];
  try {
    await store.dispatch('customerLifecycle/reorderStages', ids);
  } catch (error) {
    await store.dispatch('customerLifecycle/getStages', {
      includeArchived: true,
      force: true,
    });
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_LIFECYCLE.ERROR.REORDER')
    );
  }
};
</script>

<template>
  <div class="flex w-full flex-col gap-6">
    <PageTabs :tabs="lifecycleTabs" />
    <PageHeader
      :title="t('TELETEL_LIFECYCLE.SETTINGS.TITLE')"
      :description="t('TELETEL_LIFECYCLE.SETTINGS.DESCRIPTION')"
    />

    <div
      v-if="isLoadingSettings"
      class="flex min-h-40 items-center justify-center text-sm text-n-slate-11"
    >
      {{ t('TELETEL_LIFECYCLE.LOADING') }}
    </div>

    <ContentCard
      v-else-if="!settings.available"
      :title="t('TELETEL_LIFECYCLE.SETTINGS.UNAVAILABLE_TITLE')"
    >
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('TELETEL_LIFECYCLE.SETTINGS.UNAVAILABLE_DESCRIPTION') }}
      </p>
    </ContentCard>

    <template v-else>
      <ContentCard :title="t('TELETEL_LIFECYCLE.SETTINGS.STATUS_TITLE')">
        <label
          class="flex cursor-pointer items-start justify-between gap-4 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
        >
          <span class="min-w-0">
            <span class="block text-sm font-medium text-n-slate-12">
              {{ t('TELETEL_LIFECYCLE.SETTINGS.ENABLE_LABEL') }}
            </span>
            <span class="mt-1 block text-xs leading-5 text-n-slate-10">
              {{ t('TELETEL_LIFECYCLE.SETTINGS.ENABLE_HINT') }}
            </span>
          </span>
          <input
            type="checkbox"
            class="mt-1 size-4 flex-shrink-0"
            :checked="settings.enabled"
            :disabled="uiFlags.isSavingSettings"
            @change="toggleEnabled"
          />
        </label>
      </ContentCard>

      <template v-if="settings.enabled">
        <!-- Automatic rules: each one off by default; every move is a normal
             system transition with a reason, visible in history and reports. -->
        <ContentCard :title="t('TELETEL_LIFECYCLE.SETTINGS.RULES.TITLE')">
          <p class="mb-3 text-xs text-n-slate-10">
            {{ t('TELETEL_LIFECYCLE.SETTINGS.RULES.HINT') }}
          </p>
          <div class="flex flex-col gap-3 text-sm">
            <!-- 1. first message -->
            <div class="rounded-xl border border-n-weak p-3">
              <label class="flex cursor-pointer items-center gap-2">
                <input
                  v-model="rulesForm.first_message.enabled"
                  type="checkbox"
                  class="!mb-0"
                />
                <span class="font-medium text-n-slate-12">
                  {{
                    t('TELETEL_LIFECYCLE.SETTINGS.RULES.FIRST_MESSAGE.LABEL')
                  }}
                </span>
              </label>
              <p class="mt-1 text-xs text-n-slate-10">
                {{ t('TELETEL_LIFECYCLE.SETTINGS.RULES.FIRST_MESSAGE.HINT') }}
              </p>
              <select
                v-model="rulesForm.first_message.stage_id"
                class="mt-2 !mb-0 w-64"
              >
                <option value="">
                  {{
                    t(
                      'TELETEL_LIFECYCLE.SETTINGS.RULES.FIRST_MESSAGE.DEFAULT_STAGE'
                    )
                  }}
                </option>
                <option
                  v-for="stage in terminalOrActiveStages"
                  :key="stage.id"
                  :value="stage.id"
                >
                  {{ stage.emoji }} {{ stage.name }}
                </option>
              </select>
            </div>
            <!-- 2. label -> stage -->
            <div class="rounded-xl border border-n-weak p-3">
              <label class="flex cursor-pointer items-center gap-2">
                <input
                  v-model="rulesForm.label_to_stage.enabled"
                  type="checkbox"
                  class="!mb-0"
                />
                <span class="font-medium text-n-slate-12">
                  {{
                    t('TELETEL_LIFECYCLE.SETTINGS.RULES.LABEL_TO_STAGE.LABEL')
                  }}
                </span>
              </label>
              <p class="mt-1 text-xs text-n-slate-10">
                {{ t('TELETEL_LIFECYCLE.SETTINGS.RULES.LABEL_TO_STAGE.HINT') }}
              </p>
              <div
                v-for="(pair, index) in rulesForm.label_to_stage.pairs"
                :key="index"
                class="mt-2 flex flex-wrap items-center gap-2"
              >
                <select v-model="pair.label" class="!mb-0 w-48">
                  <option value="">
                    {{
                      t(
                        'TELETEL_LIFECYCLE.SETTINGS.RULES.LABEL_TO_STAGE.PICK_LABEL'
                      )
                    }}
                  </option>
                  <option
                    v-for="label in labels"
                    :key="label.id"
                    :value="label.title"
                  >
                    {{ label.title }}
                  </option>
                </select>
                <span
                  class="i-lucide-arrow-left size-4 text-n-slate-10 rtl:rotate-180"
                  aria-hidden="true"
                />
                <select v-model="pair.stage_id" class="!mb-0 w-48">
                  <option value="">
                    {{
                      t(
                        'TELETEL_LIFECYCLE.SETTINGS.RULES.LABEL_TO_STAGE.PICK_STAGE'
                      )
                    }}
                  </option>
                  <option
                    v-for="stage in terminalOrActiveStages"
                    :key="stage.id"
                    :value="stage.id"
                  >
                    {{ stage.emoji }} {{ stage.name }}
                  </option>
                </select>
                <button
                  type="button"
                  class="text-xs text-n-ruby-11"
                  @click="removePair(index)"
                >
                  {{
                    t('TELETEL_LIFECYCLE.SETTINGS.RULES.LABEL_TO_STAGE.REMOVE')
                  }}
                </button>
              </div>
              <button
                type="button"
                class="mt-2 text-xs font-medium text-n-brand"
                @click="addPair"
              >
                {{ t('TELETEL_LIFECYCLE.SETTINGS.RULES.LABEL_TO_STAGE.ADD') }}
              </button>
            </div>
            <!-- 3. inactivity -->
            <div class="rounded-xl border border-n-weak p-3">
              <label class="flex cursor-pointer items-center gap-2">
                <input
                  v-model="rulesForm.inactivity.enabled"
                  type="checkbox"
                  class="!mb-0"
                />
                <span class="font-medium text-n-slate-12">
                  {{ t('TELETEL_LIFECYCLE.SETTINGS.RULES.INACTIVITY.LABEL') }}
                </span>
              </label>
              <p class="mt-1 text-xs text-n-slate-10">
                {{ t('TELETEL_LIFECYCLE.SETTINGS.RULES.INACTIVITY.HINT') }}
              </p>
              <div class="mt-2 flex flex-wrap items-center gap-2">
                <input
                  v-model.number="rulesForm.inactivity.days"
                  type="number"
                  min="1"
                  max="365"
                  class="!mb-0 w-24"
                />
                <span class="text-n-slate-11">{{
                  t('TELETEL_LIFECYCLE.SETTINGS.RULES.INACTIVITY.DAYS')
                }}</span>
                <span
                  class="i-lucide-arrow-left size-4 text-n-slate-10 rtl:rotate-180"
                  aria-hidden="true"
                />
                <select
                  v-model="rulesForm.inactivity.stage_id"
                  class="!mb-0 w-48"
                >
                  <option value="">
                    {{
                      t(
                        'TELETEL_LIFECYCLE.SETTINGS.RULES.LABEL_TO_STAGE.PICK_STAGE'
                      )
                    }}
                  </option>
                  <option
                    v-for="stage in terminalOrActiveStages"
                    :key="stage.id"
                    :value="stage.id"
                  >
                    {{ stage.emoji }} {{ stage.name }}
                  </option>
                </select>
              </div>
            </div>
            <div>
              <Button
                type="button"
                size="sm"
                :label="t('TELETEL_LIFECYCLE.SETTINGS.RULES.SAVE')"
                :is-loading="rulesSaving"
                @click="saveRules"
              />
            </div>
          </div>
        </ContentCard>

        <ContentCard
          v-if="isLoadingStages"
          :title="t('TELETEL_LIFECYCLE.STAGES.TITLE')"
        >
          <p class="mb-0 text-sm text-n-slate-11" aria-live="polite">
            {{ t('TELETEL_LIFECYCLE.STAGES.LOADING') }}
          </p>
        </ContentCard>

        <ContentCard
          v-else-if="!stagesReady"
          :title="t('TELETEL_LIFECYCLE.STAGES.TITLE')"
        >
          <div
            class="flex flex-wrap items-center justify-between gap-3"
            role="alert"
          >
            <p class="mb-0 text-sm text-n-ruby-11">
              {{ t('TELETEL_LIFECYCLE.STAGES.LOAD_ERROR') }}
            </p>
            <Button
              type="button"
              size="sm"
              variant="faded"
              color="slate"
              :label="t('TELETEL_LIFECYCLE.STAGES.RETRY')"
              :aria-label="t('TELETEL_LIFECYCLE.STAGES.RETRY')"
              @click="loadStages"
            />
          </div>
        </ContentCard>

        <template v-else>
          <ContentCard :title="t('TELETEL_LIFECYCLE.STAGES.TITLE')" flush>
            <div class="flex items-center justify-between gap-3 px-4 py-3">
              <p class="mb-0 text-xs text-n-slate-10">
                {{ t('TELETEL_LIFECYCLE.STAGES.ORDER_HINT') }}
              </p>
              <label
                class="inline-flex items-center gap-2 text-xs text-n-slate-11"
              >
                <input v-model="showArchived" type="checkbox" class="mb-0" />
                {{ t('TELETEL_LIFECYCLE.STAGES.SHOW_ARCHIVED') }}
              </label>
            </div>

            <div class="overflow-x-auto">
              <table class="w-full min-w-[720px] text-sm">
                <thead
                  class="border-y border-n-weak bg-n-alpha-1 text-n-slate-10"
                >
                  <tr>
                    <th class="px-4 py-2 text-start font-medium">
                      {{ t('TELETEL_LIFECYCLE.STAGES.STAGE') }}
                    </th>
                    <th class="px-4 py-2 text-start font-medium">
                      {{ t('TELETEL_LIFECYCLE.STAGES.CATEGORY') }}
                    </th>
                    <th class="px-4 py-2 text-start font-medium">
                      {{ t('TELETEL_LIFECYCLE.STAGES.CONTACTS') }}
                    </th>
                    <th class="px-4 py-2 text-end font-medium">
                      {{ t('TELETEL_LIFECYCLE.STAGES.ACTIONS') }}
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak">
                  <tr v-for="(stage, index) in displayedStages" :key="stage.id">
                    <td class="px-4 py-3">
                      <div class="flex min-w-0 items-center gap-2">
                        <div v-if="!stage.archived_at" class="flex flex-col">
                          <button
                            type="button"
                            class="text-n-slate-9 hover:text-n-slate-12 disabled:opacity-30"
                            :disabled="index === 0 || stageMutationBusy"
                            :aria-label="
                              t('TELETEL_LIFECYCLE.STAGES.MOVE_UP', {
                                name: stage.name,
                              })
                            "
                            @click="moveStage(stage, -1)"
                          >
                            <span class="i-lucide-chevron-up size-4" />
                          </button>
                          <button
                            type="button"
                            class="text-n-slate-9 hover:text-n-slate-12 disabled:opacity-30"
                            :disabled="
                              index === activeStages.length - 1 ||
                              stageMutationBusy
                            "
                            :aria-label="
                              t('TELETEL_LIFECYCLE.STAGES.MOVE_DOWN', {
                                name: stage.name,
                              })
                            "
                            @click="moveStage(stage, 1)"
                          >
                            <span class="i-lucide-chevron-down size-4" />
                          </button>
                        </div>
                        <div class="min-w-0">
                          <LifecycleStageBadge :stage="stage" />
                          <p
                            v-if="stage.description"
                            class="mb-0 mt-1 max-w-md truncate text-xs text-n-slate-10"
                          >
                            {{ stage.description }}
                          </p>
                          <span
                            v-if="stage.is_default"
                            class="mt-1 inline-block text-xs text-n-blue-11"
                          >
                            {{ t('TELETEL_LIFECYCLE.STAGES.DEFAULT') }}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td class="px-4 py-3 text-n-slate-11">
                      {{
                        t(
                          `TELETEL_LIFECYCLE.CATEGORIES.${stage.category.toUpperCase()}`
                        )
                      }}
                    </td>
                    <td class="px-4 py-3 text-n-slate-11">
                      {{ stage.contacts_count ?? 0 }}
                    </td>
                    <td class="px-4 py-3 text-end">
                      <button
                        v-if="!stage.archived_at"
                        type="button"
                        class="me-3 text-xs font-medium text-n-blue-11 hover:underline"
                        :disabled="stageMutationBusy"
                        @click="editStage(stage)"
                      >
                        {{ t('TELETEL_LIFECYCLE.STAGES.EDIT') }}
                      </button>
                      <button
                        v-if="!stage.archived_at"
                        type="button"
                        class="text-xs font-medium text-n-ruby-11 hover:underline"
                        :disabled="stageMutationBusy"
                        @click="archiveStage(stage)"
                      >
                        {{ t('TELETEL_LIFECYCLE.STAGES.ARCHIVE') }}
                      </button>
                      <button
                        v-if="!stage.archived_at && !stage.is_default"
                        type="button"
                        class="ms-3 text-xs font-medium text-n-ruby-11 hover:underline"
                        :disabled="stageMutationBusy"
                        @click="openDeleteDialog(stage)"
                      >
                        {{ t('TELETEL_LIFECYCLE.STAGES.DELETE') }}
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!displayedStages.length">
                    <td
                      colspan="4"
                      class="px-4 py-8 text-center text-n-slate-10"
                    >
                      {{ t('TELETEL_LIFECYCLE.STAGES.EMPTY') }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </ContentCard>

          <ContentCard
            :title="
              t(
                editingId
                  ? 'TELETEL_LIFECYCLE.FORM.EDIT_TITLE'
                  : 'TELETEL_LIFECYCLE.FORM.NEW_TITLE'
              )
            "
          >
            <form class="grid gap-4" @submit.prevent="saveStage">
              <div class="grid gap-4 sm:grid-cols-[5rem_minmax(0,1fr)]">
                <label class="flex flex-col gap-1 text-sm text-n-slate-11">
                  {{ t('TELETEL_LIFECYCLE.FORM.EMOJI') }}
                  <input
                    v-model="form.emoji"
                    maxlength="8"
                    class="h-10 !mb-0 text-center"
                  />
                </label>
                <label class="flex flex-col gap-1 text-sm text-n-slate-11">
                  {{ t('TELETEL_LIFECYCLE.FORM.NAME') }}
                  <input
                    v-model="form.name"
                    maxlength="80"
                    required
                    class="h-10 !mb-0"
                  />
                </label>
              </div>

              <label class="flex flex-col gap-1 text-sm text-n-slate-11">
                {{ t('TELETEL_LIFECYCLE.FORM.DESCRIPTION') }}
                <textarea
                  v-model="form.description"
                  maxlength="240"
                  rows="2"
                  class="!mb-0"
                />
              </label>

              <div class="grid gap-4 sm:grid-cols-2">
                <label class="flex flex-col gap-1 text-sm text-n-slate-11">
                  {{ t('TELETEL_LIFECYCLE.FORM.CATEGORY') }}
                  <select v-model="form.category" class="h-10 !mb-0 text-start">
                    <option
                      v-for="category in CATEGORIES"
                      :key="category"
                      :value="category"
                      :disabled="isEditingDefault && category !== 'active'"
                    >
                      {{
                        t(
                          `TELETEL_LIFECYCLE.CATEGORIES.${category.toUpperCase()}`
                        )
                      }}
                    </option>
                  </select>
                </label>
                <label class="flex flex-col gap-1 text-sm text-n-slate-11">
                  {{ t('TELETEL_LIFECYCLE.FORM.COLOR') }}
                  <input
                    v-model="form.color"
                    type="color"
                    class="h-10 w-full !mb-0 p-1"
                  />
                </label>
              </div>

              <label
                class="inline-flex items-center gap-2 text-sm text-n-slate-11"
              >
                <input
                  v-model="form.is_default"
                  type="checkbox"
                  class="mb-0"
                  :disabled="form.category !== 'active' || isEditingDefault"
                />
                {{ t('TELETEL_LIFECYCLE.FORM.DEFAULT') }}
              </label>

              <div class="flex flex-wrap gap-2">
                <Button
                  type="submit"
                  size="sm"
                  :label="t('TELETEL_LIFECYCLE.FORM.SAVE')"
                  :is-loading="uiFlags.isSavingStage"
                  :disabled="!canSave"
                />
                <Button
                  v-if="editingId"
                  type="button"
                  size="sm"
                  variant="faded"
                  color="slate"
                  :label="t('TELETEL_LIFECYCLE.FORM.CANCEL')"
                  :disabled="stageMutationBusy"
                  @click="resetForm"
                />
              </div>
            </form>
          </ContentCard>
        </template>
      </template>
    </template>
  </div>
  <Dialog
    ref="deleteDialog"
    type="alert"
    width="xl"
    overflow-y-auto
    :title="
      t('TELETEL_LIFECYCLE.STAGES.DELETE_TITLE', {
        name: deleteTarget?.name || '',
      })
    "
    :description="t('TELETEL_LIFECYCLE.STAGES.DELETE_WARNING')"
    :confirm-button-label="
      t(
        deleteRemaining > 0 && deleteMode === 'all'
          ? 'TELETEL_LIFECYCLE.STAGES.DELETE_CONFIRM_MOVE'
          : 'TELETEL_LIFECYCLE.STAGES.DELETE_CONFIRM'
      )
    "
    :disable-confirm-button="!canConfirmDelete"
    :is-loading="deleteBusy"
    @confirm="confirmDelete"
    @close="closeDeleteDialog"
  >
    <div v-if="deleteTarget" class="flex flex-col gap-4 text-sm">
      <p v-if="deleteRemaining <= 0" class="mb-0 text-n-slate-11">
        {{ t('TELETEL_LIFECYCLE.STAGES.DELETE_NO_CONTACTS') }}
      </p>
      <template v-else>
        <p class="mb-0 font-medium text-n-slate-12">
          {{
            t('TELETEL_LIFECYCLE.STAGES.DELETE_CONTACTS_HINT', {
              count: deleteRemaining,
            })
          }}
        </p>
        <div class="flex flex-col gap-2">
          <label class="flex items-center gap-2">
            <input v-model="deleteMode" type="radio" value="all" class="mb-0" />
            <span>{{ t('TELETEL_LIFECYCLE.STAGES.DELETE_MODE_ALL') }}</span>
            <select
              v-model="deleteMoveTo"
              class="mb-0 h-8 min-w-40 rounded-lg border border-n-weak bg-n-alpha-2 px-2 text-sm"
              :disabled="deleteMode !== 'all'"
            >
              <option
                v-for="stage in deleteTargets"
                :key="stage.id"
                :value="String(stage.id)"
              >
                {{ stage.emoji ? `${stage.emoji} ` : '' }}{{ stage.name }}
              </option>
            </select>
          </label>
          <label class="flex items-center gap-2">
            <input
              v-model="deleteMode"
              type="radio"
              value="each"
              class="mb-0"
            />
            <span>{{ t('TELETEL_LIFECYCLE.STAGES.DELETE_MODE_EACH') }}</span>
          </label>
        </div>
        <div
          v-if="deleteMode === 'each'"
          class="max-h-72 overflow-y-auto rounded-lg border border-n-weak"
        >
          <div
            v-for="contact in deleteContacts"
            :key="contact.id"
            class="flex items-center justify-between gap-2 border-b border-n-weak px-3 py-2 last:border-b-0"
          >
            <span class="min-w-0 truncate text-n-slate-12">
              {{ contactLabel(contact) }}
            </span>
            <span class="flex shrink-0 items-center gap-2">
              <select
                v-model="contactTargets[contact.id]"
                class="mb-0 h-8 min-w-36 rounded-lg border border-n-weak bg-n-alpha-2 px-2 text-sm"
              >
                <option value="" disabled>
                  {{ t('TELETEL_LIFECYCLE.STAGES.DELETE_PICK_STAGE') }}
                </option>
                <option
                  v-for="stage in deleteTargets"
                  :key="stage.id"
                  :value="String(stage.id)"
                >
                  {{ stage.emoji ? `${stage.emoji} ` : '' }}{{ stage.name }}
                </option>
              </select>
              <button
                type="button"
                class="whitespace-nowrap rounded-lg border border-n-weak px-3 py-1 text-xs font-medium text-n-blue-11 hover:bg-n-alpha-2 disabled:opacity-40"
                :disabled="
                  !contactTargets[contact.id] || movingContactId === contact.id
                "
                @click="moveOneContact(contact)"
              >
                {{ t('TELETEL_LIFECYCLE.STAGES.DELETE_MOVE_ONE') }}
              </button>
            </span>
          </div>
          <p
            v-if="deleteContactsLoading"
            class="mb-0 px-3 py-2 text-xs text-n-slate-10"
          >
            {{ t('TELETEL_LIFECYCLE.LOADING') }}
          </p>
          <button
            v-else-if="deleteContactsHasMore"
            type="button"
            class="w-full px-3 py-2 text-xs font-medium text-n-blue-11 hover:underline"
            @click="loadDeleteContacts(deleteContactsPage + 1)"
          >
            {{ t('TELETEL_LIFECYCLE.STAGES.DELETE_LOAD_MORE') }}
          </button>
          <p
            v-else-if="!deleteContacts.length"
            class="mb-0 px-3 py-2 text-xs text-n-slate-10"
          >
            {{ t('TELETEL_LIFECYCLE.STAGES.DELETE_NO_CONTACTS') }}
          </p>
        </div>
        <p
          v-if="deleteMode === 'each' && deleteRemaining > 0"
          class="mb-0 text-xs text-n-amber-11"
        >
          {{
            t('TELETEL_LIFECYCLE.STAGES.DELETE_EACH_PENDING', {
              count: deleteRemaining,
            })
          }}
        </p>
      </template>
    </div>
  </Dialog>
</template>
